import { useEffect, useMemo } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";
import { useCarousel } from "nuka-carousel";
import { useCompositeState, Composite } from "reakit/Composite";

import { useFormatMessage } from "../Intl/Context";
import CarouselPagingDotsItem from "./CarouselPaginDotsItem";

// This overrides the default PagingDots component from nuka-carousel, which allows us
// to customize the dots aria-label and other accessibility and display settings.
//
// It's mainly a copy-and-paste from the original nuka-carousel component, formatted with
// our code style rules and customized.
//
const CarouselPagingDots = ({ slidesRefs, display }) => {
  const slides = slidesRefs.current;

  const t = useFormatMessage();
  const groupAriaLabel = t(intlMessages.groupAriaLabel);

  const { totalPages, currentPage, goToPage } = useCarousel();

  const indexes = useMemo(() => {
    // Create an array of indexes from 0 to totalPages - 1, which will be used to render
    // the paging dots.
    return Array.from({ length: totalPages }, (_, index) => index);
  }, [totalPages]);

  const compositeState = useCompositeState();
  const { items: compositeItems, setCurrentId, currentId } = compositeState;
  const currentItem = compositeItems[currentPage];

  const className = classNames("slider-paging-dots-list", { hidden: !display });

  // Synchronize the `compositeState.currentId` prop with the carousel's `currentSlide`
  // when another control source (autoplay, arrows, keyboard arrows navigation) makes the
  // current slide change, to ensure that when the user tabs into the dots, the current
  // slide's dot is properly focused.
  useEffect(() => {
    if (compositeItems.length === 0 || !currentItem || currentItem.id === currentId) {
      return;
    }

    setCurrentId(currentItem.id);

    // Note : We only want to react to when the currentSlide index is changed from the
    //        outside, and we need to reflect it to the compositeState. If we also added
    //        the currentId and setCurrentId dependencies, the effect would be called even
    //        when then user navigates between the dots, and would prevent it to work
    //        properly.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compositeItems, currentItem]);

  return (
    <Composite
      {...compositeState}
      as="ul"
      role="tablist"
      aria-label={groupAriaLabel}
      className={className}
    >
      {indexes.map(index => {
        const normalizedIndex = index % totalPages;

        return (
          <CarouselPagingDotsItem
            key={index}
            index={normalizedIndex}
            currentSlide={currentPage}
            slideCount={totalPages}
            slides={slides}
            compositeState={compositeState}
            goToSlide={goToPage}
          />
        );
      })}
    </Composite>
  );
};

export default CarouselPagingDots;

const intlMessages = defineMessages({
  groupAriaLabel: {
    id: "carousel.pagingDots.groupAriaLabel",
    defaultMessage: "Choisir une diapositive à afficher"
  }
});
